import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AtSign, CornerUpLeft, Eye, CheckCheck, Inbox as InboxIcon } from "lucide-react";
import { api } from "@/platform/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "mention", label: "Mentions" },
  { key: "reply", label: "Replies" },
  { key: "ready_for_review", label: "Ready for Review" },
];

const TYPE_META = {
  mention: { label: "Mention", icon: AtSign, color: "#A99BFF" },
  reply: { label: "Reply", icon: CornerUpLeft, color: "#4F9CFF" },
  ready_for_review: { label: "Ready for Review", icon: Eye, color: "#00C2E0" },
};

function relTime(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function headline(n) {
  const num = n.ticketNumber ? `#${n.ticketNumber}` : "a ticket";
  if (n.type === "mention") return `${n.actorName} mentioned you in ${num}`;
  if (n.type === "reply") return `${n.actorName} replied to ${num}`;
  return `${num} is ready for review`;
}

const notifyChanged = () => { try { window.dispatchEvent(new Event("pinlayr:notif")); } catch (_e) { /* ignore */ } };

export default function Inbox() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (type) => {
    const q = type && type !== "all" ? `?type=${type}&limit=30` : "?limit=30";
    try {
      const res = await api.get(`/api/notifications${q}`);
      setItems(res.notifications || []);
      setHasMore(!!res.hasMore);
    } catch (_e) { setItems([]); }
  }, []);

  useEffect(() => { setItems(null); load(filter); }, [filter, load]);
  useEffect(() => {
    const onFocus = () => load(filter);
    window.addEventListener("focus", onFocus);
    const i = setInterval(() => load(filter), 25000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(i); };
  }, [filter, load]);

  const loadMore = async () => {
    if (!items?.length) return;
    setLoadingMore(true);
    const before = items[items.length - 1].createdAt;
    const type = filter !== "all" ? `type=${filter}&` : "";
    try {
      const res = await api.get(`/api/notifications?${type}limit=30&before=${encodeURIComponent(before)}`);
      setItems((cur) => [...(cur || []), ...(res.notifications || [])]);
      setHasMore(!!res.hasMore);
    } catch (_e) { /* ignore */ }
    setLoadingMore(false);
  };

  const unread = useMemo(() => (items || []).filter((n) => !n.readAt).length, [items]);

  const openItem = async (n) => {
    if (!n.readAt) {
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      try { await api.patch(`/api/notifications/${n.id}/read`); notifyChanged(); } catch (_e) { /* ignore */ }
    }
    if (n.available) {
      navigate(`/app/projects/${n.projectId}?tab=Tickets&ticket=${n.ticketId}`);
    }
  };

  const markOne = async (e, n) => {
    e.stopPropagation();
    setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
    try { await api.patch(`/api/notifications/${n.id}/read`); notifyChanged(); } catch (_e) { /* ignore */ }
  };

  const markAll = async () => {
    setItems((cur) => (cur || []).map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    try { await api.post("/api/notifications/read-all"); notifyChanged(); } catch (_e) { /* ignore */ }
  };

  return (
    <div className="mx-auto max-w-3xl animate-in fade-in duration-300" data-testid="inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight text-[#F4F5F8]">Inbox</h1>
          <p className="mt-0.5 text-[14px] text-[#98A0B0]">Activity that needs your attention across your projects.</p>
        </div>
        <button onClick={markAll} disabled={unread === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#2A3040] px-3 py-2 text-[13px] font-medium text-[#98A0B0] transition-colors hover:text-[#F4F5F8] disabled:opacity-40"
          data-testid="inbox-mark-all">
          <CheckCheck className="h-4 w-4" /> Mark all as read
        </button>
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-[#242A38]">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap border-b-2 px-3.5 py-2.5 text-[13.5px] font-medium transition-colors ${filter === f.key ? "border-[#6D5EF6] text-[#F4F5F8]" : "border-transparent text-[#6E7686] hover:text-[#AAB1BF]"}`}
            data-testid={`inbox-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {items === null ? (
          <div className="grid place-items-center py-20"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#12151D] p-14 text-center" data-testid="inbox-empty">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}><InboxIcon className="h-7 w-7" style={{ color: "#A99BFF" }} /></div>
            {filter === "all" ? (
              <>
                <p className="mt-4 text-[16px] font-semibold text-[#F4F5F8]">You're all caught up</p>
                <p className="mt-1.5 text-[13.5px] text-[#98A0B0]">Mentions, replies and items ready for review will appear here.</p>
              </>
            ) : (
              <p className="mt-4 text-[15px] font-medium text-[#C4C9D4]">No {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} yet</p>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[#242A38]" data-testid="inbox-list">
            {items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.reply;
              const Icon = meta.icon;
              const unreadRow = !n.readAt;
              return (
                <button key={n.id} onClick={() => openItem(n)}
                  className={`flex w-full items-start gap-3 border-b border-[#1C2130] px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-[#171B29] ${unreadRow ? "bg-[#141A28]" : "bg-[#12151D]"}`}
                  data-testid={`inbox-item-${n.id}`} data-unread={unreadRow ? "1" : "0"}>
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${meta.color}22`, color: meta.color }}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                      {unreadRow && <span className="h-1.5 w-1.5 rounded-full bg-[#6D5EF6]" data-testid="inbox-unread-dot" />}
                      <span className="ml-auto whitespace-nowrap text-[11.5px] text-[#565E70]">{relTime(n.createdAt)}</span>
                    </div>
                    <p className={`mt-1 truncate text-[13.5px] ${unreadRow ? "font-semibold text-[#F4F5F8]" : "text-[#C4C9D4]"}`}>{headline(n)}</p>
                    <p className="truncate text-[12.5px] text-[#98A0B0]">
                      <span className="text-[#7B8496]">{n.projectName}</span>
                      {n.ticketTitle ? <span> · {n.ticketTitle}</span> : null}
                    </p>
                    {n.messagePreview && n.type !== "ready_for_review" && (
                      <p className="mt-1 truncate text-[12.5px] italic text-[#6E7686]">“{n.messagePreview}”</p>
                    )}
                    {!n.available && <p className="mt-1 text-[11.5px] text-[#7A5310]">This ticket is no longer available.</p>}
                  </div>
                  {unreadRow && (
                    <span onClick={(e) => markOne(e, n)} className="shrink-0 text-[11.5px] font-medium text-[#6E7686] hover:text-[#A99BFF]" data-testid={`inbox-mark-${n.id}`}>Mark read</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {hasMore && items && items.length > 0 && (
          <div className="mt-4 text-center">
            <button onClick={loadMore} disabled={loadingMore} className="rounded-lg border border-[#2A3040] px-4 py-2 text-[13px] font-medium text-[#98A0B0] hover:text-[#F4F5F8] disabled:opacity-40" data-testid="inbox-load-more">
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
